/**
 * Adapter to convert between calculator module data structures and existing university data structures
 */

import { JupasProgram } from '../types';
import type { ProgrammeEntry } from '@/lib/core/types';
import { PROGRAMMES_DATA as PROGRAMS } from './data';

// Map inst_code to university key
const INST_CODE_TO_UNI_KEY: Record<string, string> = {
  'HKU': 'hku',
  'CUHK': 'cuhk',
  'PolyU': 'polyu',
  'CityU': 'cityu',
  'HKBU': 'hkbu',
  'Lingnan': 'lingnan',
  'EdUHK': 'eduhk',
  'HKUST': 'hkust',
  'HKMU': 'hkmu',
  'hkmu2': 'hkmu',
  'SSSDP': 'sssdp'
};

/**
 * Convert JupasProgram to ProgrammeEntry format
 */
export function convertJupasProgramToProgrammeEntry(program: JupasProgram): ProgrammeEntry {
  return {
    inst_code: program.inst_code || '',
    inst_prefix: program.inst_prefix || '',
    inst_second: program.inst_second || '',
    jupas_slug: program.jupas_slug,
    university: program.university,
    programmeDetails: {
      id: String(program.jupas_id),
      name_eng: program.name_eng,
      name_tch: program.name_tch,
      jupas_url: program.jupas_url || '',
      prog_url: program.prog_url || '',
      first_year_intake: String(program.first_year_intake || ''),
      faculty_eng: program.faculty_eng || '',
      faculty_tch: program.faculty_tch || '',
      studyPeriod: Number(program.study_period) || undefined,
      interview_eng: program.interview_eng,
      interview_tch: program.interview_tch,
      interviewRequired: Boolean(program.interview_eng && program.interview_eng !== 'No'),
      study_period: String(program.study_period || ''),
      new_programme: Boolean(program.new_programme),
    },
    method_eng: program.programme_notes || '',
    method_tch: '',
    interview_eng: program.interview_eng,
    interview_tch: program.interview_tch,
    calculation: {
      conditions: [],
      specificSubjectWeighting: program.subject_weightings || {},
      programmeScoringFormula: program.programme_notes || '',
      specialRequirements: '',
      programmeWeighting: program.programme_notes || '',
      formulaWeight: program.programme_notes || '',
      comments: program.programme_notes || '',
      subjectMer: program.subject_mer || '',
    },
    expectedScore: 0, // Will be calculated based on past scores
    otherRequirements_eng: [],
    otherRequirements_tch: [],
    past_admission_scores: (program.past_admission_scores || []).map(score => ({
      year: score.year,
      UQ: score.UQ,
      HA: score.HA,
      MED: score.MED,
      LQ: score.LQ,
    })),
  };
}

/**
 * Group programs by university from PROGRAMS array
 */
export function groupProgramsByUniversity(programs: any[] = PROGRAMS) {
  const grouped: Record<string, ProgrammeEntry[]> = {
    hku: [],
    cuhk: [],
    polyu: [],
    cityu: [],
    hkbu: [],
    lingnan: [],
    eduhk: [],
    hkust: [],
    hkmu: [],
    sssdp: [],
  };

  programs.forEach(program => {
    // Use inst_code field (not university) to determine which group
    const instCode = program.inst_code || program.university;
    const uniKey = INST_CODE_TO_UNI_KEY[instCode] || instCode?.toLowerCase() || 'other';
    
    if (grouped[uniKey]) {
      grouped[uniKey].push(convertJupasProgramToProgrammeEntry(program));
    } else {
      console.warn(`Unknown university key: ${uniKey} for inst_code: ${instCode}`);
    }
  });

  return grouped;
}

/**
 * Create allUniversities compatible structure from PROGRAMS
 */
export const allUniversitiesFromPrograms = groupProgramsByUniversity(PROGRAMS);

/**
 * Get program by JUPAS ID
 */
export function getProgramByJupasId(jupasId: string): any | undefined {
  const normalizedId = String(jupasId).replace(/^JS/i, '');
  return PROGRAMS.find(p => String(p.jupas_id) === String(jupasId) || String(p.jupas_id) === normalizedId);
}

/**
 * Get all programs for a specific university
 */
export function getProgramsByUniversity(university: string): any[] {
  const uniKey = university.toLowerCase();
  // Match by inst_code mapped to university key
  return PROGRAMS.filter((p: any) => {
    const instCode = p.inst_code || p.university;
    const programUniKey = INST_CODE_TO_UNI_KEY[instCode] || instCode?.toLowerCase();
    return programUniKey === uniKey;
  });
}
